import React, { useMemo } from 'react';
import { FiClock } from 'react-icons/fi';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from '../../contexts/TranslationContext';

export type TimePeriod = '7d' | '30d' | '3m' | 'ytd' | 'all';

interface RoiTimelinePoint {
  time: string;
  roi_at_this_time: number;
}

interface Props {
  data: RoiTimelinePoint[];
  cashProfit?: number;
  timePeriod?: TimePeriod;
  onTimePeriodChange?: (period: TimePeriod) => void;
  fromDate?: Date;
  toDate?: Date;
}

function fmtDateRange(from: Date, to: Date): string {
  const fromStr = from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const toStr = to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fromStr} — ${toStr}`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function fmtRoi(v: number): string {
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
}

function fmtCash(v: number): string {
  const abs = Math.abs(v);
  const formatted = abs >= 1000
    ? `$${(abs / 1000).toFixed(2)}k`
    : `$${abs.toFixed(2)}`;
  return `${v >= 0 ? '+' : '-'}${formatted}`;
}

const COLORS = {
  primary: '#58C9FA',
  grid: 'rgba(240,242,245,0.08)',
  textMuted: '#8B949E',
  loss: '#f85149',
};

const ExpertRoiTimelineChart: React.FC<Props> = ({
  data,
  cashProfit = 0,
  timePeriod,
  onTimePeriodChange,
  fromDate,
  toDate,
}) => {
  const { t } = useTranslation();

  const periodOptions = useMemo(
    (): { value: TimePeriod; label: string }[] => [
      { value: '7d', label: t.charts.period7d },
      { value: '30d', label: t.charts.period30d },
      { value: '3m', label: t.charts.period3m },
      { value: 'ytd', label: t.charts.periodYtd },
      { value: 'all', label: t.charts.periodAll },
    ],
    [t],
  );

  const isPositive = cashProfit >= 0;
  const lineColor = isPositive ? COLORS.primary : COLORS.loss;
  const cashColor = isPositive ? COLORS.primary : COLORS.loss;

  return (
    <div className="roi-chart">
      <div className="roi-chart__header">
        <span className="roi-chart__cash" style={{ color: cashColor }}>
          {fmtCash(cashProfit)}
        </span>
        <span className="roi-chart__hint">{t.expertStats.stakeHint}</span>
      </div>

      {fromDate && toDate && timePeriod && (
        <div className="roi-chart__meta">
          <div className="roi-chart__dates">
            <FiClock size={12} />
            <span>{fmtDateRange(fromDate, toDate)}</span>
          </div>
          <div className="roi-chart__period-filter">
            {periodOptions.map(({ value, label }) => (
              <button
                key={value}
                className={`roi-chart__period-btn${timePeriod === value ? ' roi-chart__period-btn--active' : ''}`}
                onClick={() => onTimePeriodChange?.(value)}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {data.length === 0 ? (
        <div className="roi-chart__empty">{t.expertStats.noResolvedPicksInPeriod}</div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={data} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="roiGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={lineColor} stopOpacity={0.25} />
                <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={COLORS.grid} />
            <XAxis
              dataKey="time"
              tickFormatter={fmtDate}
              tick={{ fill: COLORS.textMuted, fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              interval={Math.max(1, Math.floor(data.length / 5))}
            />
            <YAxis hide />
            <Tooltip
              contentStyle={{
                background: '#161B22',
                border: '1px solid rgba(240,242,245,0.12)',
                borderRadius: 8,
                fontSize: 12,
                color: '#F0F2F5',
              }}
              labelFormatter={(label: unknown) => fmtDate(String(label))}
              formatter={(value: unknown) => [fmtRoi(Number(value)), t.common.roi]}
              cursor={{ stroke: COLORS.grid, strokeWidth: 1 }}
            />
            <Area
              type="monotone"
              dataKey="roi_at_this_time"
              stroke={lineColor}
              strokeWidth={2}
              fill="url(#roiGradient)"
              dot={false}
              activeDot={{ r: 4, fill: lineColor, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default ExpertRoiTimelineChart;
